import { Modal, ModalProps } from '@/modules/common/components/Modal/Modal';
import { MoneyInput } from '@/modules/common/components/MoneyInput';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import {
  integerToMoney,
  moneyToInteger,
} from '@/modules/common/lib/moneyHandler';
import { IEmployee } from '@/types/employer';
import { ITeam } from '@/types/team';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

export type UserTarget = {
  variant: 'user';
  user: Pick<IEmployee, 'firstName' | 'lastName' | 'email' | '_id'> & {
    profile: Pick<IEmployee['profile'], 'learningBudgetBalance'>;
  };
};

export type TeamTarget = {
  variant: 'team';
  team: Pick<ITeam, 'name' | '_id'>;
};

export type LearningBudgetTarget = UserTarget | TeamTarget;

export type LearningBudgetAllocationChange = {
  allocationOffset: number;
  confirmation: boolean;
};

type LearningBudgetAllocationModalProps = Omit<
  ModalProps,
  'title' | 'subtitle' | 'onClose'
> & {
  onClose: (data?: LearningBudgetAllocationChange) => void;
  isLoading: boolean;
  target: LearningBudgetTarget | null;
};

const DELTA = 5000;

export function LearningBudgetAllocationModal({
  onClose,
  target,
  isLoading,
  ...props
}: LearningBudgetAllocationModalProps) {
  const currentBalance =
    target?.variant === 'user'
      ? target?.user?.profile?.learningBudgetBalance ?? 0
      : 0;

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    reset,
    formState: { isValid, isSubmitting, errors },
  } = useForm<LearningBudgetAllocationChange>({
    disabled: isLoading,
    defaultValues: {
      allocationOffset: 0,
      confirmation: false,
    },
    resolver: yupResolver(
      yup
        .object()
        .shape({
          allocationOffset: yup
            .number()
            .required()
            .notOneOf([0], 'Please enter an amount different than zero'),
          confirmation: yup.boolean().required().oneOf([true]),
        })
        .test(({ allocationOffset }) => {
          if (
            target?.variant === 'user' &&
            currentBalance + allocationOffset < 0
          ) {
            throw new yup.ValidationError(
              'This action would result in a negative Learning Budget balance. Please adjust the amount.',
              allocationOffset,
              'allocationOffset',
            );
          }
          return true;
        }),
    ),
  });

  useEffect(() => {
    reset();
  }, [props.open, reset]);

  if (!target) {
    return null;
  }

  const targetName =
    target.variant === 'user'
      ? [target.user.firstName, target.user.lastName].filter(Boolean).join(' ')
      : target.team.name;

  const targetInfo = target.variant === 'user' ? target.user.email : '';
  const allocationOffset = +watch('allocationOffset');

  const finalBalance =
    target.variant === 'team'
      ? 0
      : (target.user?.profile?.learningBudgetBalance ?? 0) +
        (allocationOffset || 0);
  const operation = allocationOffset > 0 ? 'add' : 'remove';
  const confirmationText =
    operation === 'add'
      ? 'I confirm this increase in the Learning Budget allocation.'
      : 'I confirm this decrease in the Learning Budget allocation.';

  const handleCancel = () => onClose(undefined);
  const addFunds = () =>
    setValue('allocationOffset', allocationOffset + DELTA, {
      shouldValidate: true,
    });
  const removeFunds = () =>
    setValue('allocationOffset', allocationOffset - DELTA, {
      shouldValidate: true,
    });

  return (
    <Modal
      {...props}
      aria-describedby="modal-description"
      onClose={handleCancel}
      title={`Manage Learning Budget funds for ${targetName}`}
      subTitle={
        target.variant === 'user'
          ? 'Add or remove Learning Budget funds assigned to an employee.'
          : 'Add or remove Learning Budget funds for all team members.'
      }
      actions={[
        <Button
          type="button"
          variant="outlined"
          onClick={handleCancel}
          key="cancel"
          disabled={isSubmitting}
        >
          Cancel
        </Button>,
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid}
          loading={isLoading || isSubmitting}
          key="confirm"
          type="submit"
          form="learning-budget-allocation-form"
        >
          Confirm changes
        </Button>,
      ]}
      classes={{
        paper: 'max-w-[543px]',
      }}
    >
      <form
        id="learning-budget-allocation-form"
        onSubmit={e => handleSubmit(onClose)(e)}
      >
        <Stack gap={3}>
          <Box>
            <Typography fontWeight={600}>{targetName}</Typography>
            <Typography color="textSecondary">{targetInfo}</Typography>
          </Box>
          {target.variant === 'user' && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Current Balance
              </Typography>
              <Typography
                variant="h4"
                fontWeight={600}
                className={isLoading ? 'animate-pulse' : ''}
              >
                {formatCurrencyValue(currentBalance.toString(), 'eur', {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Box>
          )}
          <Stack gap={1}>
            <Typography
              component="label"
              htmlFor="allocationOffset"
              variant="subtitle2"
              fontWeight={600}
            >
              Add / Remove Funds
            </Typography>
            <Stack direction="row" alignItems="stretch">
              <Button
                type="button"
                disabled={isLoading || isSubmitting}
                size="large"
                sx={{
                  borderRadius: 0,
                  width: 'auto',
                  minWidth: '0 !important',
                }}
                className="rounded-r-none px-3 py-4"
                onClick={removeFunds}
                title="Remove Funds"
                aria-label={`Click to deduct ${formatCurrencyValue(
                  DELTA.toString(),
                  'eur',
                )} from ${
                  target.variant === 'user'
                    ? 'the user Learning Budget'
                    : "each team member's Learning Budget"
                }`}
              >
                <RemoveIcon />
              </Button>
              <Controller
                control={control}
                name="allocationOffset"
                render={({
                  field: { value, onChange, ref: inputRef, ...field },
                }) => {
                  return (
                    <MoneyInput
                      value={integerToMoney(value)}
                      onChange={e => {
                        onChange(moneyToInteger(e.target.value));
                      }}
                      {...field}
                      inputRef={inputRef}
                      id="allocationOffset"
                      decimalScale={2}
                      startAdornment={
                        <Typography
                          className={`text-2xl font-semibold ${
                            isLoading || isSubmitting ? 'text-gray-400' : ''
                          }`}
                        >
                          €
                        </Typography>
                      }
                      allowNegative={true}
                      classes={{
                        root: 'flex-1 rounded-none text-center',
                        input: 'text-center text-2xl font-semibold',
                        focused: 'outline-none',
                      }}
                    />
                  );
                }}
              />
              <Button
                type="button"
                size="large"
                disabled={isLoading || isSubmitting}
                sx={{
                  borderRadius: 0,
                  width: 'auto',
                  minWidth: '0 !important',
                }}
                onClick={addFunds}
                className="rounded-l-none px-3 py-4"
                title="Add Funds"
                aria-label={`Click to add ${formatCurrencyValue(
                  DELTA.toString(),
                  'eur',
                )} to ${
                  target.variant === 'user'
                    ? 'the user Learning Budget'
                    : "each team member's Learning Budget"
                }`}
              >
                <AddIcon />
              </Button>
            </Stack>
            {errors?.allocationOffset?.message && (
              <FormHelperText error={true}>
                {errors?.allocationOffset?.message}
              </FormHelperText>
            )}
          </Stack>
          {!errors?.allocationOffset && allocationOffset !== 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                Final Balance
              </Typography>
              <Typography className="text-sm">
                {target.variant === 'user' && (
                  <span className="text-lg font-semibold">
                    {formatCurrencyValue(Math.max(finalBalance, 0), 'eur', {
                      maximumFractionDigits: 2,
                    })}{' '}
                  </span>
                )}
                {finalBalance >= 0 && (
                  <>
                    {operation === 'add' ? (
                      <>
                        <Typography
                          id="modal-description"
                          aria-label={`${formatCurrencyValue(
                            allocationOffset.toString(),
                            'eur',
                          )} will be added to the ${
                            target.variant === 'user'
                              ? 'current budget'
                              : "each team member's current budget"
                          }${
                            target.variant === 'user'
                              ? `, totaling ${formatCurrencyValue(
                                  Math.max(finalBalance, 0).toString(),
                                  'eur',
                                )}`
                              : ''
                          }`}
                          component="span"
                          color="success.dark"
                          bgcolor="success.light"
                          className="px-[3px] py-[2px] rounded font-semibold"
                        >
                          +
                          {formatCurrencyValue(allocationOffset, 'eur', {
                            maximumFractionDigits: 2,
                          })}
                        </Typography>{' '}
                        will be{' '}
                        <Typography
                          component="span"
                          color="success.dark"
                          className="font-semibold text-sm"
                        >
                          added
                        </Typography>{' '}
                        {target.variant === 'user'
                          ? 'to the current budget.'
                          : "to each team member's current budget."}
                      </>
                    ) : (
                      <>
                        <Typography
                          component="span"
                          color="error.main"
                          bgcolor="error.light"
                          className="px-[3px] py-[2px] rounded font-semibold"
                        >
                          {formatCurrencyValue(allocationOffset, 'eur', {
                            maximumFractionDigits: 2,
                          })}
                        </Typography>{' '}
                        will be{' '}
                        <Typography
                          color="error.main"
                          component="span"
                          className="font-semibold text-sm"
                        >
                          deducted
                        </Typography>{' '}
                        {target.variant === 'user'
                          ? 'from the current budget.'
                          : "from each team member's current budget."}
                      </>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          )}
          <FormControlLabel
            control={
              <Controller
                control={control}
                name="confirmation"
                render={({ field: { value, onChange, ...field } }) => (
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={value}
                    onChange={e => onChange(e.target.checked)}
                    {...field}
                  />
                )}
              />
            }
            label={<span className="text-sm">{confirmationText}</span>}
          />
        </Stack>
      </form>
    </Modal>
  );
}
